/**
 * Recursively removes empty and falsy values from objects, arrays, and FormData.
 * Handles plain objects, arrays and FormData instances.
 * 
 * @param {*} value - The value to clean (object, array, FormData, or primitive)
 * @returns {*} The cleaned value with empty/falsy properties/elements removed
 */
export const removeEmpty = (value) => {
  if (value instanceof FormData) {
    // convert to plain object first
    const obj = {}
    for (const [k, v] of value.entries()) {
      // if multiple entries with same key, accumulate into array
      if (Object.prototype.hasOwnProperty.call(obj, k)) {
        obj[k] = Array.isArray(obj[k]) ? obj[k].concat(v) : [obj[k], v]
      } else {
        obj[k] = v
      }
    }
    return removeEmpty(obj)
  }

  if (Array.isArray(value)) {
    const cleanedArr = value
      .map(removeEmpty)
      .filter(v => {
        // Remove falsy values (including 0), empty objects, and empty arrays
        if (!v) return false // removes null, undefined, '', false, NaN, 0
        if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false // empty objects
        if (Array.isArray(v) && v.length === 0) return false // empty arrays
        return true
      })
    return cleanedArr
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [k, v]) => {
      const cleaned = removeEmpty(v)
      
      // Remove falsy values (including 0), empty objects, and empty arrays
      if (!cleaned) return acc // removes null, undefined, '', false, NaN, 0
      const isEmptyObject = cleaned && typeof cleaned === 'object' && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0
      if (isEmptyObject) return acc // empty objects
      if (Array.isArray(cleaned) && cleaned.length === 0) return acc // empty arrays
      
      acc[k] = cleaned
      return acc
    }, {})
  }

  return value
}
